import { levelFromPercent } from "@/lib/skill-levels";
import { useEscoEnabled } from "./useEscoEnabled";
import { EscoLanguageSelect } from "./EscoLanguageSelect";
import type { EscoLanguage } from "@/lib/esco";
import { useId, useState } from "react";
import { FormattedMessage, useIntl } from "react-intl";
import { Button } from "@/components/design/Button";
import { readClassification, selectedClassification, type EscoConcept } from "@/lib/esco";
import { EscoOccupationSkills } from "./EscoSearch";

export function EscoProfileSkills({ experiences, skills, onAdd, onPreview }: {
  experiences: readonly Record<string, unknown>[];
  skills: readonly Record<string, unknown>[];
  onAdd: (payload: Record<string, unknown>) => void;
  onPreview?: (preview: { description: string; level: number } | null) => void;
}) {
  const enabled = useEscoEnabled();
  const intl = useIntl();
  const id = useId();
  const [language, setLanguage] = useState<EscoLanguage>(intl.locale.startsWith("de") ? "de" : "en");
  const [active, setActive] = useState<string | null>(null);
  const [filter, setFilter] = useState("");
  const [lastAdded, setLastAdded] = useState<{ label: string; level: number } | null>(null);
  const lastLevel = levelFromPercent(lastAdded?.level);
  const occupations = [...new Map(experiences.flatMap(e => (readClassification(e.classification)?.references ?? [])
    .filter(r => r.status === "CONFIRMED" && r.basedOnText === e.jobTitle)).map(r => [r.uri, r])).values()];
  const selected = skills.flatMap(e => readClassification(e.classification)?.references.map(r => r.uri) ?? []);
  if (!enabled || !occupations.length) return null;
  function add(c: EscoConcept, level: number) {
    if (selected.includes(c.uri) || skills.some(s => String(s.description ?? "").toLocaleLowerCase() === c.label.toLocaleLowerCase())) return;
    const classification = selectedClassification(c, c.label);
    onAdd({ description: c.label, level, classification: JSON.stringify({ ...classification, entryOrigin: "ESCO_SELECTION" }) });
    setLastAdded({ label: c.label, level });
  }
  return <div className="my-3">
    <Button id={`${id}-suggest`} variant="chip" size="sm" aria-expanded={!!active} aria-controls={`${id}-suggestions`}
      onClick={() => { setFilter(""); setLastAdded(null); setActive(active ? null : occupations[0]!.uri); }}>
      {active ? <FormattedMessage id="esco.hideSuggestions" defaultMessage="Hide suggestions" />
        : <FormattedMessage id="esco.suggestSkills" defaultMessage="Suggest skills" />}
    </Button>
    {active ? <section id={`${id}-suggestions`} aria-label={intl.formatMessage({ id: "esco.suggestSkills", defaultMessage: "Suggest skills" })}
      className="mt-3 space-y-3 border-t border-ink/10 pt-3">
        <p className="smycles-field-meta" role="status">{lastAdded
          ? lastLevel ? <FormattedMessage id="esco.addedAtLevel" defaultMessage="{name} added at {level}." values={{ name: lastAdded.label, level: intl.formatMessage({ id: lastLevel.nameId, defaultMessage: lastLevel.name }) }} />
            : <FormattedMessage id="esco.addedUnrated" defaultMessage="{name} added under Unrated." values={{ name: lastAdded.label }} />
          : <FormattedMessage id="esco.chooseSkillLevelHint" defaultMessage="Select a skill, then choose its level." />}</p>
        {occupations.length > 1 ? <select id={`${id}-occupation`} value={active} onChange={e => { setActive(e.target.value); setFilter(""); }}
          aria-label={intl.formatMessage({ id: "esco.chooseOccupation", defaultMessage: "Choose an occupation" })}
          className="smycles-field-value w-full rounded-md bg-surface px-3 py-2 ring-1 ring-ink-ui">
          {occupations.map(o => <option key={o.uri} value={o.uri}>{o.label}</option>)}
        </select> : <p className="smycles-field-meta">{occupations[0]!.label}</p>}
        <div className="flex flex-wrap items-end gap-3">
        <input id={`${id}-filter`} value={filter} onChange={e => setFilter(e.target.value)}
          aria-label={intl.formatMessage({ id: "esco.filterSkills", defaultMessage: "Filter skills" })}
          placeholder={intl.formatMessage({ id: "esco.filterSkills", defaultMessage: "Filter skills" })}
          className="smycles-field-value min-w-0 flex-[1_1_14rem] rounded-md bg-surface px-3 py-2 ring-1 ring-ink-ui" />
        <EscoLanguageSelect value={language} onChange={setLanguage} />
        </div>
        <EscoOccupationSkills language={language} uri={active} filter={filter} onPreview={onPreview} onSelect={add} selected={selected} existingLabels={skills.map(s => String(s.description ?? ""))} />
    </section> : null}
  </div>;
}
